import {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import clsx from 'clsx';
import {Popover} from '@mantine/core';

import {InputProps} from './types';
import Icon, {Icons} from '../Icon';
import Alert, {AlertTypes} from '../display/Alert';
import s from './styles.module.scss';

const WARNING_SHOW_TIME = 5000; // 5 sec

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      error,
      name,
      label,
      required,
      type,
      placeholder,
      icon,
      rightIcon,
      className,
      price,
      hint,
      warnText,
      changeFn,
      successMessage,
      containerClassName,
      size = 'normal',
      ...rest
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [textLength, setTextLength] = useState(0);
    const [isShowWarning, setIsShowWarning] = useState(false);

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const showErrorMessage = typeof error === 'string' && error.length > 0;

    const inputClassName = clsx(
      s['input-container'],
      {
        [s['input-container-with-icon']]: icon,
        [s['input-container-with-right-icon']]: rightIcon,
      },
      containerClassName
    );

    // Show warning only 5 sec
    useEffect(() => {
      if (!isShowWarning) return;

      const timeout = setTimeout(() => {
        setIsShowWarning(false);
      }, WARNING_SHOW_TIME);

      return () => {
        clearTimeout(timeout);
      };
    }, [isShowWarning]);

    useEffect(() => {
      const input = inputRef.current;

      if (!rest.maxLength) {
        return;
      }

      const handler = (event: any) => {
        setTextLength(event.target.value.length);
      };
      input?.addEventListener('input', handler);

      if (inputRef.current) {
        setTextLength(inputRef.current?.value.length);
      }

      return () => {
        input?.removeEventListener('input', handler);
      };
    }, [rest.maxLength]);

    useEffect(() => {
      const input = inputRef.current;

      if (!price || !input) {
        return;
      }

      // For price input add numbers after dot if need it
      const blurHandler = ({target}: any) => {
        const replacedString: string = target.value.replaceAll(',', '.');

        if (replacedString.includes('.')) {
          const parts = replacedString.split('.');

          let leaf = '';
          for (let i = 0; i < 2; i++) {
            leaf += parts[1][i] || '0';
          }

          // eslint-disable-next-line no-param-reassign
          target.value = `${parts[0] || '0'}.${leaf}`;
        } else if (replacedString.length) {
          // eslint-disable-next-line no-param-reassign
          target.value = `${replacedString}.00`;
        }
      };

      input.addEventListener('blur', blurHandler);

      return () => {
        input.removeEventListener('blur', blurHandler);
      };
    }, [price]);

    useEffect(() => {
      const input = inputRef.current;

      if (!changeFn || !input) {
        return;
      }

      const changeHandler = ({target}: any) => {
        if (!changeFn) {
          return;
        }
        const prevValue = target.value;
        const newValue = changeFn(prevValue);

        if (newValue !== prevValue) {
          // eslint-disable-next-line no-param-reassign
          target.value = newValue;
          setTextLength(newValue.length);
          setIsShowWarning(true);
        }
        input.dispatchEvent(new Event('change'));
      };

      input.addEventListener('input', changeHandler);

      return () => {
        input.addEventListener('input', changeHandler);
      };
    }, [changeFn]);

    return (
      <div
        className={clsx(
          s.input,
          {
            [s.large]: size === 'large',
          },
          className
        )}
      >
        {label && (
          <div className={s['label-content']}>
            <label htmlFor={name}>
              {label} {required && <span>*</span>}
            </label>
            {hint && (
              <Popover position="bottom" width={250} withArrow>
                <Popover.Target>
                  <div style={{display: 'flex', cursor: 'pointer'}}>
                    <Icon name={Icons.questionCircle} className={s['hint-icon']} />
                  </div>
                </Popover.Target>

                <Popover.Dropdown>
                  <div className={s['input-hint']}>{hint}</div>
                </Popover.Dropdown>
              </Popover>
            )}
          </div>
        )}
        <div className={inputClassName}>
          {icon && <div className={s['input-icon']}>{icon}</div>}
          <input
            {...rest}
            type={type || 'text'}
            name={name}
            placeholder={placeholder}
            ref={inputRef}
          />
          {rightIcon && <div className={s['input-icon-right']}>{rightIcon}</div>}
        </div>
        {showErrorMessage || rest.maxLength ? (
          <div className={s.info}>
            <span
              className={clsx(s.message, {
                [s['message--success']]: Boolean(successMessage),
                [s['message--error']]: showErrorMessage,
              })}
            >
              {error || successMessage}
            </span>
            {rest.maxLength && (
              <div className={s.counter}>
                {textLength} / {rest.maxLength}
              </div>
            )}
          </div>
        ) : null}
        {isShowWarning && warnText && (
          <Alert
            type={AlertTypes.warning}
            className={s.alert}
            message="Emoji are not allowed and have been removed"
          />
        )}
      </div>
    );
  }
);

export default Input;
